import "core-js/modules/es6.reflect.construct.js";
import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
var _excluded = ["onEnter", "onEntering", "onEntered", "onExit", "onExiting", "className"];

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

import classNames from 'classnames';
import css from 'dom-helpers/style';
import React from 'react';
import PropTypes from 'prop-types';
import Transition from './Transition';
import capitalize from './util/capitalize';
import { createChainedFunction } from '../../wui-core/src';
var MARGINS = {
  height: ['marginTop', 'marginBottom'],
  width: ['marginLeft', 'marginRight']
}; // reading a dimension prop will cause the browser to recalculate,
// which will let our animations work

function triggerBrowserReflow(node) {
  node.offsetHeight; // eslint-disable-line no-unused-expressions
}

function getDimensionValue(dimension, elem) {
  var value = elem["offset".concat(capitalize(dimension))];
  var margins = MARGINS[dimension];
  return value + parseInt(css(elem, margins[0]), 10) + parseInt(css(elem, margins[1]), 10);
}

var propTypes = {
  /**
   * Show the component; triggers the expand or collapse animation
   */
  in: PropTypes.bool,

  /**
   * Unmount the component (remove it from the DOM) when it is collapsed
   */
  unmountOnExit: PropTypes.bool,

  /**
   * Run the expand animation when the component mounts, if it is initially
   * shown
   */
  transitionAppear: PropTypes.bool,

  /**
   * Duration of the collapse animation in milliseconds, to ensure that
   * finishing callbacks are fired even if the original browser transition end
   * events are canceled
   */
  timeout: PropTypes.number,

  /**
   * Callback fired before the component expands
   */
  onEnter: PropTypes.func,

  /**
   * Callback fired after the component starts to expand
   */
  onEntering: PropTypes.func,

  /**
   * Callback fired after the component has expanded
   */
  onEntered: PropTypes.func,

  /**
   * Callback fired before the component collapses
   */
  onExit: PropTypes.func,

  /**
   * Callback fired after the component starts to collapse
   */
  onExiting: PropTypes.func,

  /**
   * Callback fired after the component has collapsed
   */
  onExited: PropTypes.func,

  /**
   * The dimension used when collapsing, or a function that returns the
   * dimension
   *
   * _Note: Bootstrap only partially supports 'width'!
   * You will need to supply your own CSS animation for the `.width` CSS class._
   */
  dimension: PropTypes.oneOfType([PropTypes.oneOf(['height', 'width']), PropTypes.func]),

  /**
   * Function that returns the height or width of the animating DOM node
   *
   * Allows for providing some custom logic for how much the Collapse component
   * should animate in its specified dimension. Called with the current
   * dimension prop value and the DOM node.
   */
  getDimensionValue: PropTypes.func,

  /**
   * ARIA role of collapsible element
   */
  role: PropTypes.string
};
var defaultProps = {
  in: false,
  timeout: 300,
  unmountOnExit: false,
  transitionAppear: false,
  dimension: 'height',
  getDimensionValue: getDimensionValue
};

var Collapse = /*#__PURE__*/function (_React$Component) {
  _inherits(Collapse, _React$Component);

  var _super = _createSuper(Collapse);

  function Collapse(props, context) {
    var _this;

    _classCallCheck(this, Collapse);

    _this = _super.call(this, props, context);
    _this.handleEnter = _this.handleEnter.bind(_assertThisInitialized(_this));
    _this.handleEntering = _this.handleEntering.bind(_assertThisInitialized(_this));
    _this.handleEntered = _this.handleEntered.bind(_assertThisInitialized(_this));
    _this.handleExit = _this.handleExit.bind(_assertThisInitialized(_this));
    _this.handleExiting = _this.handleExiting.bind(_assertThisInitialized(_this));
    return _this;
  }
  /* -- Expanding -- */


  _createClass(Collapse, [{
    key: "handleEnter",
    value: function handleEnter(elem) {
      var dimension = this._dimension();

      elem.style[dimension] = '0';
    }
  }, {
    key: "handleEntering",
    value: function handleEntering(elem) {
      var dimension = this._dimension();

      elem.style[dimension] = this._getScrollDimensionValue(elem, dimension);
    }
  }, {
    key: "handleEntered",
    value: function handleEntered(elem) {
      var dimension = this._dimension();

      elem.style[dimension] = null;
    }
    /* -- Collapsing -- */

  }, {
    key: "handleExit",
    value: function handleExit(elem) {
      var dimension = this._dimension();

      elem.style[dimension] = this.props.getDimensionValue(dimension, elem) + 'px';
      triggerBrowserReflow(elem);
    }
  }, {
    key: "handleExiting",
    value: function handleExiting(elem) {
      var dimension = this._dimension();

      elem.style[dimension] = '0';
    }
  }, {
    key: "_dimension",
    value: function _dimension() {
      return typeof this.props.dimension === 'function' ? this.props.dimension() : this.props.dimension;
    } // for testing

  }, {
    key: "_getScrollDimensionValue",
    value: function _getScrollDimensionValue(elem, dimension) {
      return "".concat(elem["scroll".concat(capitalize(dimension))], "px");
    }
  }, {
    key: "render",
    value: function render() {
      var _this$props = this.props,
          onEnter = _this$props.onEnter,
          onEntering = _this$props.onEntering,
          onEntered = _this$props.onEntered,
          onExit = _this$props.onExit,
          onExiting = _this$props.onExiting,
          className = _this$props.className,
          props = _objectWithoutProperties(_this$props, _excluded);

      delete props.dimension;
      delete props.getDimensionValue;
      var handleEnter = createChainedFunction(this.handleEnter, onEnter);
      var handleEntering = createChainedFunction(this.handleEntering, onEntering);
      var handleEntered = createChainedFunction(this.handleEntered, onEntered);
      var handleExit = createChainedFunction(this.handleExit, onExit);
      var handleExiting = createChainedFunction(this.handleExiting, onExiting);
      var classes = {
        width: this._dimension() === 'width'
      };
      return /*#__PURE__*/React.createElement(Transition, _extends({}, props, {
        "aria-expanded": props.role ? props.in : null,
        className: classNames(className, classes),
        exitedClassName: "collapse",
        exitingClassName: "collapsing",
        enteredClassName: "collapse in",
        enteringClassName: "collapsing",
        onEnter: handleEnter,
        onEntering: handleEntering,
        onEntered: handleEntered,
        onExit: handleExit,
        onExiting: handleExiting
      }));
    }
  }]);

  return Collapse;
}(React.Component);

Collapse.propTypes = propTypes;
Collapse.defaultProps = defaultProps;
export default Collapse;


// WEBPACK FOOTER //
// ./~/@tinper/next-ui/lib/wui-transition/src/Collapse.js